/* eslint import/no-dynamic-require:0 */

import Backbone from 'backbone';
import Mn from 'backbone.marionette';
import Radio from 'backbone.radio';
import $ from 'jquery';
import app from './app';
import IntegrationCallBackView from './integrations/callback';
import MainView from './main';
import execute_demo_script from './predictions';
import SidebarView from './sidebar';
import { isIntegrationActive, snakeCaseToHumanReadable } from './utils';
import { getCurrentUser, getCurrentTenant } from './auth/userContext';
import { initializeNewLayout } from './common/components/layout/new-layout-wrapper';
import {
  isV2User,
  hasRole,
  hasModule,
  canSee,
  DATA_STUDIO_TENANT,
} from './common/utils/rbac';
import { getNewAdminUrl } from './common/utils/urls';

import { initiateOAuthFlow } from './auth/oauth';

require('backbone.epoxy');

const channel = Radio.channel('integration');

// The Insights Reports feature has been deprecated and removed from the app
// navigation. These report pages must remain inaccessible to everyone (including
// superkudus). The `evr_insights` rendering engine is still used by non-deprecated
// pages (Predictions deep dives, mapping outputs), so we block only these report slugs.
const DEPRECATED_INSIGHTS_REPORT_PAGES = [
  'l1_leadsource',
  'l2',
  'l3',
  'l3_hubspot',
  'champion_buyer_analysis',
  'happy_path',
  'persona_analysis',
];

const Controller = (Mn.Object as any).extend({
  async initialize() {
    // Note: Keeping app.session temporarily for backward compatibility
    // Will be removed once all references are updated
    this.session = app.session;

    // Initialize tenant from session if available, otherwise create empty tenant
    this.tenant = app.session.tenant;

    // If tenant is not populated yet, try to get it from cached user data
    // This ensures tenant is available early in the router lifecycle
    if (!this.tenant.get('_id')) {
      try {
        const tenant = await getCurrentTenant();
        if (tenant && tenant._id) {
          this.session.update_tenant({ _id: tenant._id });

          // Also ensure user data is populated so role extraction works correctly
          // getCurrentUser() will sync to app.session which extracts role from permissions
          try {
            await getCurrentUser();
          } catch (error) {
            // User data will be fetched when route executes
          }
        }
      } catch (error) {
        // Tenant will be fetched when route is accessed via execute() or direct()
      }
    }

    this.manual_connections = {};
    this.layout_set = false;
    this.demo_set = false;
    this.set_listeners();
  },

  set_listeners() {
    channel.on('success', (results: any) => {
      if (results && results.alert) {
        app.notifications.show(
          `Successfully connected to <b> ${results.integration}</b>`,
          {
            dismiss_timeout: 10000,
          }
        );
      }
      const tenant = this.tenant.get('_id');
      Backbone.history.navigate(`/org/${tenant}/integrations`);
      this.manual_connections[results.integration] = true;
      this.integrations(tenant);
    });

    channel.on('cancel', () => {
      window.history.back();
    });
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async execute(callback: () => {}, args: any[], _name: string) {
    // Check if we're already redirecting to OAuth (prevent loop)
    if (sessionStorage.getItem('oauth_redirecting') === 'true') {
      return; // Don't execute callback, redirect is in progress
    }

    try {
      // Lightweight auth check - just try to fetch user data
      const user = await getCurrentUser(); // Will throw if not authenticated

      const currentFragment = Backbone.history.getFragment();

      // Conversion and audience mapping are Data Studio features (RGI-2693):
      // a v2 user whose org did not buy the module (Sales Copilot only) is sent
      // back to the Mapping overview. SuperKudu and v1 users pass through the
      // canSee bypass. Standard users never get here — set_layout already
      // bounces them to the new admin.
      const dataStudioMappingRoute = currentFragment.match(
        /^org\/([^/]+)\/mapping\/(conversion_mapping|audiences)($|\/.*)/
      );
      if (dataStudioMappingRoute && !canSee(user, DATA_STUDIO_TENANT)) {
        Backbone.history.navigate(`/org/${dataStudioMappingRoute[1]}/mapping`, {
          trigger: true,
          replace: true,
        });
        return;
      }

      // Regex to match 'org/:tenant/integrations' and any sub-paths like 'org/:tenant/integrations/:integration/...'
      const isIntegrationsRoute = /^org\/[^/]+\/integrations($|\/.*)/.test(
        currentFragment
      );

      if (isIntegrationsRoute) {
        // Role extraction logic:
        // user.role is resolved by legacyRole() in model_session.ts update_user():
        // rbac.role on v2 tenants (orgAdmin -> 'admin'), otherwise the permission
        // matching the current tenant._id, defaulting to 'user'
        // Ensure app.session is populated (via getCurrentUser() sync) before checking role

        // Ensure user.role is populated by checking if app.session has user data
        // If not, getCurrentUser() already synced it, but role extraction requires tenant
        const tenantId = this.tenant.get('_id');
        if (!tenantId) {
          // Tenant not available - can't determine role, deny access
          Backbone.history.navigate('/', { trigger: true, replace: true });
          return;
        }

        // Ensure user.role is populated (extracted from permissions for current tenant)
        // If role is not set, try to get user from session which should have role extracted
        let userRole = user.role;
        if (!userRole) {
          const sessionUser = app.session.get('user');
          userRole = sessionUser?.role;
        }

        // If still no role, user might not have permissions for this tenant
        // Default to denying access
        const canAccessIntegrations =
          user &&
          (userRole === 'admin' ||
            userRole === 'architect' ||
            user.superKudu === true);

        if (!canAccessIntegrations) {
          // Redirect unauthorized users to the root/home page.
          // 'replace: true' ensures the forbidden URL is not added to browser history.
          Backbone.history.navigate('/', { trigger: true, replace: true });
          return; // Stop further processing of this forbidden route
        }
      }

      if (callback) callback.apply(this, args);
    } catch (error) {
      // Not authenticated or 401 error
      const currentPath = window.location.pathname + window.location.search;
      const { pathname } = window.location;

      // Only store redirect if it's not OAuth callback (public routes handled by their routers)
      if (pathname !== '/oauth/callback' && currentPath.length > 1) {
        sessionStorage.setItem('oauth_redirect', currentPath);
      }
      initiateOAuthFlow();
      // Don't execute callback - redirect is happening
    }
  },

  get_flags() {
    const flags: UnknownObject = {};
    this.tenant.flags.forEach((flag: any) => {
      flags[flag.get('name')] = flag.get('active');
    });
    return flags;
  },

  set_demo() {
    if (this.demo_set) {
      return;
    }
    const demo = this.tenant.flags.get('demo');
    if (demo && demo.get('active') === true) {
      execute_demo_script();
    }
    this.demo_set = true;
  },

  async set_layout() {
    if (this.layout_set) {
      // If layout is already set, ensure main view is still initialized
      if (!this.main) {
        const flags = this.get_flags();
        this.renderMain(flags);
      }
      return;
    }
    const flags = this.get_flags();
    const tenantId = this.tenant.get('_id');
    const user = this.session.get('user');
    const primaryTenantId = user?.permissions?.[0]?.tenant;

    // V2 RBAC access gate: standards (non-orgAdmin, non-SuperKudu) must use
    // the new admin (mk-admin-app). The old admin doesn't have the per-module
    // gating and would otherwise grant them admin-level access.
    //
    // Exception: until Predictions and Score Lookup (profiles) are migrated,
    // the new admin links Data Studio standard users back here for them. Those
    // pages are exactly what the data_studio module grants, so let module
    // holders reach them; every other route still bounces to the new admin.
    if (isV2User(user) && !hasRole(user, 'orgAdmin') && !user?.superKudu) {
      const isDataStudioPage =
        hasModule(user, 'data_studio') &&
        new RegExp(`^/org/${tenantId}/(predictions|profiles)(/|$)`).test(
          window.location.pathname
        );

      if (!isDataStudioPage) {
        const fromPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const target = `${getNewAdminUrl()}/org/${tenantId}?from=${encodeURIComponent(
          fromPath
        )}`;
        window.location.replace(target);
        return;
      }
    }

    // Initialize new layout with React components
    try {
      initializeNewLayout({
        tenantId: String(tenantId),
        primaryTenantId: primaryTenantId ? String(primaryTenantId) : undefined,
        flags,
        user: user
          ? {
              email: user.email,
              superKudu: user.superKudu,
              role: user.role,
              permissions: user.permissions,
              rbac: user.rbac,
            }
          : undefined,
      });
    } catch (error) {
      // Fallback to old sidebar if new layout fails
      this.sidebar = new SidebarView({
        model: this.session,
        flags,
      });
      app.root.showChildView('sidebar', this.sidebar);
    }

    // Keep the old sidebar view instance for compatibility (but don't show it)
    // Only create if new layout didn't fail
    if (!this.sidebar) {
      this.sidebar = new SidebarView({
        model: this.session,
        flags,
      });
    }
    // Hide the old sidebar - new layout handles it
    // Don't show it in the DOM, but keep the view instance for compatibility

    this.renderMain(flags);
    app.notifications.show_sidebar();

    this.layout_set = true;
  },

  renderMain(existingFlags: string[]) {
    const flags = existingFlags || this.get_flags();
    app.root.detachChildView('main');
    this.main = new MainView({
      flags,
    });
    app.root.showChildView('main', this.main);
  },

  assign(view: any, selector: string) {
    view.setElement(this.main.$(selector));
  },

  capitalize_first(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  },

  change_title(title: string) {
    const newTitle = title ? `${title} | MadKudu` : 'HG Insights';
    $(document).attr('title', newTitle);
  },

  track_page(title: string) {
    if (!title || !window.analytics) return;

    const user = app.session.get('user');
    if (!user?.email) return;

    // Event-scoped properties only. email/domain/role are user traits, sent on
    // identify by model_session.track() (CPF-1199).
    return window.analytics.page(title, {
      tenant: this.tenant.get('_id'),
      app: 'addax',
      path: window.location.pathname,
    });
  },

  async fetch_tenant() {
    // if the tenant is not initialized, get the data from the server
    if (!this.tenant.is_fetched()) {
      await this.tenant.fetch();
    }
  },

  async setup(tenant: number) {
    if (tenant !== this.tenant.get('_id')) {
      // if the tenant has changed, force a change
      this.session.update_tenant({
        _id: tenant,
      });
      this.layout_set = false;
    }
    try {
      await this.fetch_tenant();
    } catch (err) {
      // if request fails, redirect to the corresponding error page
      if (err.status === 404) {
        // note, if a user tries to access a page he doesn't have access to, we actually return a 404 (to avoid leaking existence of resources)
        Backbone.history.navigate('error/404', {
          trigger: true,
        });
      } else {
        throw err;
      }
    }
    await this.set_layout();
    this.set_demo();
  },

  update_page(page: string, subpage: string, paramTitle: string) {
    const title = paramTitle || page;
    const newTitle = title
      ? this.capitalize_first(snakeCaseToHumanReadable(title))
      : undefined;
    this.change_title(newTitle);
    this.track_page(newTitle);
    this.sidebar.highlight(page, subpage);
  },

  async check_superkudu(): Promise<void> {
    // Note: Keeping app.session temporarily for backward compatibility
    // This method will be updated once all session references are migrated
    const user =
      app.session.get('user') ||
      (await getCurrentUser().catch((): null => null));
    if (!user || !user.superKudu) {
      window.location.href = '/';
    }
  },

  /**
   * Hot reload mechanism
   * Don't change the string interpolations into variables! It's a hack for allowing a dynamic import.
   * @param {*} tenant
   * @param {*} viewPath
   * @param {*} page
   * @param {*} title
   */
  hot_reload_view(viewPath: string, transition_view: () => {}) {
    if ((module as any).hot) {
      (module as any).hot.accept(require.resolve(`${viewPath}`), () => {
        transition_view();
      });
    }
    transition_view();
  },

  async transition_view(
    paramTenant: string,
    View: any,
    params: { tenant?: number; model?: string } = {},
    options: { page?: string; subpage?: string; title?: string } = {}
  ) {
    const tenant =
      typeof paramTenant === 'string' ? Number(paramTenant) : paramTenant;
    const { page, subpage, title } = options;
    await this.setup(tenant);
    this.update_page(page, subpage, title);
    const newParams = params;
    newParams.tenant = tenant;
    newParams.model = this.tenant;

    // Ensure main view is initialized before trying to show child view
    if (!this.main) {
      const flags = this.get_flags();
      this.renderMain(flags);
    }

    if (!this.main) {
      throw new Error('Main view not initialized');
    }

    // eslint-disable-next-line
    this.main.showChildView(
      'content', // eslint-disable-next-line
      View.default ? new View.default(newParams) : new View(newParams)
    );
  },

  transition_view_without_tenant(
    View: any,
    params = {},
    options: { title?: string } = {}
  ) {
    const { title } = options;
    this.change_title(title);
    // eslint-disable-next-line
    app.root.showChildView(
      'main', // eslint-disable-next-line
      View.default ? new View.default(params) : new View(params)
    );
  },

  async direct() {
    // Get tenant ID from cached data or session
    let tenantId = this.tenant.get('_id');

    if (!tenantId) {
      try {
        const tenant = await getCurrentTenant();
        tenantId = tenant._id;
        if (tenantId) {
          this.session.update_tenant({ _id: tenantId });
        }
      } catch (error) {
        // Try to get from session
        const sessionTenant = app.session.get('tenant');
        if (sessionTenant && sessionTenant._id) {
          tenantId = sessionTenant._id;
          this.session.update_tenant({ _id: tenantId });
        }
      }
    }

    if (tenantId) {
      this.home(tenantId, {});
    } else {
      Backbone.history.navigate('error/404', { trigger: true });
    }
  },

  async home(tenant: number, query_params: {}) {
    await this.setup(tenant); // since this is first page, force a fetch
    const onboarding_intercom = this.tenant.flags.get('onboarding_intercom');
    const onboarding_hubspot = this.tenant.flags.get('onboarding_hubspot');
    const onboarding_googlesheet = this.tenant.flags.get(
      'onboarding_googlesheet'
    );
    if (onboarding_intercom && onboarding_intercom.get('active') === true) {
      this.onboarding_intercom(tenant, query_params);
    } else if (
      onboarding_hubspot &&
      onboarding_hubspot.get('active') === true
    ) {
      this.onboarding_hubspot(tenant, query_params);
    } else if (
      onboarding_googlesheet &&
      onboarding_googlesheet.get('active') === true
    ) {
      this.onboarding_googlesheet(tenant, query_params);
    } else {
      this.homepage(tenant);
    }
  },

  help(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/help`);
  },

  homepage(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}`);
  },
  dataStudio(tenant: number) {
    this.hot_reload_view('./data-studio/index', () =>
      this.transition_view(
        tenant,
        require('./data-studio/index'),
        {},
        {
          page: 'data-studio',
          title: 'Data Studio',
        }
      )
    );
  },

  evrInsights(tenant: number, page: string, tab: string) {
    if (DEPRECATED_INSIGHTS_REPORT_PAGES.includes(page)) {
      Backbone.history.navigate('error/404', { trigger: true });
      return;
    }
    let newPage = page;

    this.hot_reload_view('./evr_insights/engine/page', async () => {
      if (newPage === 'l3') {
        const isHubspotActive = await isIntegrationActive(tenant, 'hubspot');
        const isSalesforceActive = await isIntegrationActive(
          tenant,
          'salesforce'
        );
        if (isHubspotActive && !isSalesforceActive) {
          newPage = 'l3_hubspot';
        }
      }
      this.transition_view(
        tenant,
        require('./evr_insights/engine/page'),
        {
          page: newPage,
          tab,
        },
        {
          page: `insights`,
        }
      );
    });
  },
  evrInsightsConfiguration(tenant: number, page: string) {
    if (DEPRECATED_INSIGHTS_REPORT_PAGES.includes(page)) {
      Backbone.history.navigate('error/404', { trigger: true });
      return;
    }
    this.hot_reload_view('./evr_insights/configuration/page', () =>
      this.transition_view(
        tenant,
        require('./evr_insights/configuration/page'),
        {
          page,
        },
        {
          page: `insights`,
        }
      )
    );
  },
  // The Insights Reports landing page is deprecated. Redirect any direct/bookmarked
  // access (including superkudus) to the 404 page instead of rendering it.
  evr() {
    Backbone.history.navigate('error/404', { trigger: true });
  },

  onboarding_intercom(tenant: number, query_params: {}) {
    this.hot_reload_view('./onboarding/intercom/intercom', () =>
      this.transition_view(
        tenant,
        require('./onboarding/intercom/intercom'),
        {
          query_params,
        },
        {
          page: 'get-started',
          title: 'Onboarding - Intercom',
        }
      )
    );
  },

  onboarding_hubspot(tenant: number, query_params: {}) {
    this.hot_reload_view('./onboarding/hubspot/hubspot', () =>
      this.transition_view(
        tenant,
        require('./onboarding/hubspot/hubspot'),
        {
          query_params,
        },
        {
          page: 'get-started',
          title: 'Onboarding - Hubspot',
        }
      )
    );
  },

  onboarding_googlesheet(tenant: number, query_params: {}) {
    this.hot_reload_view('./onboarding/googlesheet/googlesheet', () =>
      this.transition_view(
        tenant,
        require('./onboarding/googlesheet/googlesheet'),
        {
          query_params,
        },
        {
          page: 'get-started',
          title: 'Onboarding - Googlesheet',
        }
      )
    );
  },

  onboarding_googlesheet_redirection() {
    const tenant = this.tenant.get('_id');
    Backbone.history.navigate(`/org/${tenant}/get-started/googlesheet`, {
      trigger: true,
    });
  },

  chatbot() {
    const tenant = this.tenant.get('_id');
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/chatbot`);
  },

  preferences() {
    const tenant = this.tenant.get('_id');
    window.location.assign(
      `${NEW_ADMIN_URL}/org/${tenant}/preferences/persona`
    );
  },

  user_settings() {
    const tenant = this.tenant.get('_id');
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/user-settings`);
  },

  market_analyser() {
    const tenant = this.tenant.get('_id');
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/market-analyser`);
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  integrations(tenant: number, query_params: {}) {
    // Redirect all integrations page to new admin-app
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/integrations`);
  },

  integration_callback(integration: string, query_params: {}) {
    this.transition_view_without_tenant(
      IntegrationCallBackView,
      {
        integration,
        query_params,
      },
      {
        page: 'integrations callback',
        subpage: integration,
        title: integration,
      }
    );
  },

  integration_setup_without_tenant(integration: string) {
    const redirectUrl = `/org/${this.tenant.get(
      '_id'
    )}/integrations/${integration}${window.location.search}`;
    Backbone.history.navigate(redirectUrl, {
      trigger: true,
    });
  },
  integration_setup(tenant: number, integration: string, query_params: {}) {
    // Array of integrations that should be redirected to new admin-app
    const INTEGRATIONS_TO_REDIRECT = ['salesforce', 'hubspot', 'segment'];

    // Redirect specified integration pages to new admin-app (strict URL matching)
    if (INTEGRATIONS_TO_REDIRECT.includes(integration)) {
      const currentPath = window.location.pathname;

      // Define exact paths that should be redirected
      const basePath = `/org/${tenant}/integrations/${integration}`;
      const pullPath = `/org/${tenant}/integrations/${integration}/pull`;
      const pushPath = `/org/${tenant}/integrations/${integration}/push`;

      if (currentPath === pullPath || currentPath === `${pullPath}/`) {
        // Redirect pull page to new admin-app with pull tab
        window.location.assign(
          `${NEW_ADMIN_URL}/org/${tenant}/integrations/${integration}?tab=pull`
        );
        return;
      }
      if (currentPath === pushPath || currentPath === `${pushPath}/`) {
        // Redirect push page to new admin-app with push tab
        window.location.assign(
          `${NEW_ADMIN_URL}/org/${tenant}/integrations/${integration}?tab=push`
        );
        return;
      }
      if (currentPath === basePath || currentPath === `${basePath}/`) {
        // Redirect base integration page to new admin-app
        window.location.assign(
          `${NEW_ADMIN_URL}/org/${tenant}/integrations/${integration}`
        );
        return;
      }

      // All other paths (like admin, configure, etc.) continue with legacy
    }

    this.hot_reload_view('./integrations/view_one', () =>
      this.transition_view(
        tenant,
        require('./integrations/view_one'),
        {
          integration,
          query_params,
          model: this.tenant,
          manuallyConnected: this.manual_connections[integration],
        },
        {
          page: 'integrations',
          subpage: integration,
          title: integration,
        }
      )
    );
  },
  integration_admin(tenant: number, integration: string, query_params: {}) {
    this.hot_reload_view('./integrations/view_admin', () =>
      this.transition_view(
        tenant,
        require('./integrations/view_admin'),
        {
          integration,
          query_params,
          model: this.tenant,
        },
        {
          page: 'integrations',
          subpage: integration,
          title: integration,
        }
      )
    );
  },
  integration_configure(tenant: number, integration: string, query_params: {}) {
    this.hot_reload_view('./integrations/configure', () =>
      this.transition_view(
        tenant,
        require('./integrations/configure'),
        {
          integration,
          query_params,
          model: this.tenant,
        },
        {
          page: 'integrations',
          subpage: integration,
          title: integration,
        }
      )
    );
  },
  integration_configure_sample(
    tenant: number,
    integration: string,
    query_params: {}
  ) {
    this.hot_reload_view('./integrations/configure', () =>
      this.transition_view(
        tenant,
        require('./integrations/configure'),
        {
          integration,
          query_params,
          model: this.tenant,
          showSample: true,
        },
        {
          page: 'integrations',
          subpage: integration,
          title: integration,
        }
      )
    );
  },
  integration_pull_config(
    tenant: number,
    integration: string,
    object: string,
    query_params: {}
  ) {
    this.hot_reload_view('./integrations/admin_pages/view_mapping_config', () =>
      this.transition_view(
        tenant,
        require('./integrations/admin_pages/view_mapping_config'),
        {
          integration,
          query_params,
          model: this.tenant,
          object,
        },
        {
          page: 'integrations',
          subpage: integration,
          title: integration,
        }
      )
    );
  },
  integration_workbench(tenant: number, integration: string, query_params: {}) {
    this.hot_reload_view('./integrations/admin_pages/view_workbench', () =>
      this.transition_view(
        tenant,
        require('./integrations/admin_pages/view_workbench'),
        {
          integration,
          query_params,
          model: this.tenant,
        },
        {
          page: 'integrations workbench',
          subpage: integration,
          title: integration,
        }
      )
    );
  },

  // Campaigns

  campaigns(tenant: number, campaign_id: string) {
    this.hot_reload_view('./campaigns', () =>
      this.transition_view(
        tenant,
        require('./campaigns'),
        {
          campaign_id,
        },
        {
          render: false,
          page: 'settings',
          subpage: 'campaigns',
        }
      )
    );
  },

  advanced_campaigns(tenant: number, campaign_id: string) {
    this.hot_reload_view('./campaigns/view_all', () =>
      this.transition_view(
        tenant,
        require('./campaigns/view_all'),
        {
          campaign_id,
        },
        {
          render: false,
          page: 'settings',
          subpage: 'campaigns',
        }
      )
    );
  },

  // Settings and admins

  settings_admin(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/settings/admin`);
  },
  settings_api_usage(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/settings/usage`);
  },
  settings_api_usage_admin(tenant: number) {
    this.hot_reload_view('./settings/api_usage_admin', () =>
      this.transition_view(
        tenant,
        require('./settings/api_usage_admin'),
        {
          model: this.tenant,
        },
        {
          page: 'settings',
          subpage: 'usage',
          title: 'API Usage',
        }
      )
    );
  },
  settings_billing(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/settings/account`);
  },
  settings_billing_page(tenant: number) {
    this.hot_reload_view('./settings/billing', () =>
      this.transition_view(
        tenant,
        require('./settings/billing'),
        {},
        {
          render: false,
          page: 'settings',
          subpage: 'billing',
          title: 'Billing',
        }
      )
    );
  },
  settings_billing_admin(tenant: number) {
    this.hot_reload_view('./settings/billing_admin', () =>
      this.transition_view(
        tenant,
        require('./settings/billing_admin'),
        {},
        {
          render: false,
          page: 'settings',
          subpage: 'billing',
          title: 'Billing',
        }
      )
    );
  },

  settings_account(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/settings/account`);
  },
  settings_authentication(tenant: number) {
    window.location.assign(
      `${NEW_ADMIN_URL}/org/${tenant}/settings/authentication`
    );
  },
  settings_users(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/settings/users`);
  },
  settings_privacy(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/settings/privacy`);
  },
  flags(tenant: number, name: string) {
    if (name) {
      window.location.assign(
        `${NEW_ADMIN_URL}/org/${tenant}/settings/flags/${name}`
      );
    } else {
      window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/settings/flags`);
    }
  },

  processes(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/settings/processes`);
  },
  push(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/settings/push`);
  },

  // prototypes

  prototypes(tenant: number) {
    this.hot_reload_view('./prototypes/index', () =>
      this.transition_view(
        tenant,
        require('./prototypes/index'),
        {},
        {
          page: 'settings',
          subpage: 'Prototypes',
          title: 'Prototypes',
        }
      )
    );
  },
  sqline_ui(tenant: number) {
    this.hot_reload_view('./prototypes/sqline_ui', () =>
      this.transition_view(
        tenant,
        require('./prototypes/sqline_ui'),
        {},
        {
          page: 'prototypes',
          title: 'YAML to SQL',
        }
      )
    );
  },

  // demo section
  profiles(tenant: number, queryString: string) {
    this.hot_reload_view('./profiles/index', () => {
      this.transition_view(
        tenant,
        require('./profiles'),
        { queryString },
        {
          page: 'profiles',
          title: 'Profiles',
        }
      );
    });
  },
  predictions(tenant: number) {
    this.hot_reload_view('./predictions/predictions', () =>
      this.transition_view(
        tenant,
        require('./predictions/predictions'),
        {},
        {
          page: 'predictions',
          title: 'Predictions',
        }
      )
    );
  },
  performances(tenant: number, id: string) {
    const flags = this.get_flags();
    this.hot_reload_view('./predictions/performances/main', () =>
      this.transition_view(
        tenant,
        require('./predictions/performances/main'),
        {
          id,
          flags,
        },
        {
          page: 'predictions',
          subpage: 'performances',
          title: 'Performances',
        }
      )
    );
  },
  diagnostics(tenant: number, id: string) {
    const flags = this.get_flags();
    this.hot_reload_view('./predictions/performances/diagnostics', () =>
      this.transition_view(
        tenant,
        require('./predictions/performances/diagnostics'),
        {
          id,
          flags,
        },
        {
          page: 'diagnostics',
          subpage: 'diagnostics',
          title: 'Diagnostics',
        }
      )
    );
  },
  diagnostics_lead_grade(tenant: number, id: string) {
    const flags = this.get_flags();
    this.hot_reload_view('./predictions/performances/diagnostics', () =>
      this.transition_view(
        tenant,
        require('./predictions/performances/diagnostics'),
        {
          id,
          flags,
          context: 'lead_grade',
        },
        {
          page: 'diagnostics',
          subpage: 'diagnostics',
          title: 'Diagnostics',
        }
      )
    );
  },
  diagnostics_model(tenant: number, id: string, lead_grade: string) {
    const flags = this.get_flags();
    const context = lead_grade ? 'lead_grade' : '';
    this.hot_reload_view('./predictions/modelWeightAndOverrides', () =>
      this.transition_view(
        tenant,
        require('./predictions/modelWeightAndOverrides'),
        {
          id,
          flags,
          context,
        },
        {
          page: 'diagnostics_model',
          subpage: 'diagnostics',
          title: 'Model',
        }
      )
    );
  },
  model_edit(tenant: number, id: string) {
    const flags = this.get_flags();
    this.hot_reload_view('./predictions/model', () =>
      this.transition_view(
        tenant,
        require('./predictions/model'),
        {
          id,
          flags,
        },
        {
          page: 'model_edit',
          title: 'Edit model',
        }
      )
    );
  },
  data_overview(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/data`);
  },
  mapping_overview(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}/mapping`);
  },
  attribute_mapping_overview(tenant: number) {
    this.hot_reload_view('./mapping/attribute_mapping', () =>
      this.transition_view(
        tenant,
        require('./mapping/attribute_mapping'),
        {},
        {
          page: 'mapping',
          title: 'Attribute Mapping',
        }
      )
    );
  },

  attribute_mapping_configuration_simple(tenant: number) {
    this.hot_reload_view(
      './mapping/attribute_mapping/configurationSection/simple',
      () =>
        this.transition_view(
          tenant,
          require('./mapping/attribute_mapping/configurationSection/simple'),
          {},
          {
            page: 'mapping',
            title: 'Attribute Mapping',
          }
        )
    );
  },

  attribute_mapping_configuration_custom(tenant: number) {
    this.hot_reload_view(
      './mapping/attribute_mapping/configurationSection/custom',
      () =>
        this.transition_view(
          tenant,
          require('./mapping/attribute_mapping/configurationSection/custom'),
          {},
          {
            page: 'mapping',
            title: 'Attribute Mapping',
          }
        )
    );
  },

  event_mapping(tenant: number) {
    this.hot_reload_view('./mapping/event_mapping', () =>
      this.transition_view(
        tenant,
        require('./mapping/event_mapping'),
        {},
        {
          page: 'mapping',
          title: 'Event Mapping',
        }
      )
    );
  },
  event_mapping_parent(tenant: number, connector: string) {
    this.hot_reload_view(`./mapping/event_mapping/event_mapping_parent`, () =>
      this.transition_view(
        tenant,
        require(`./mapping/event_mapping/event_mapping_parent`),
        {
          connector,
        },
        {
          page: 'event_mapping',
          title: `${this.capitalize_first(connector)} Mapping`,
        }
      )
    );
  },
  audience_mapping(tenant: number) {
    this.hot_reload_view('./mapping/audiences', () =>
      this.transition_view(
        tenant,
        require('./mapping/audiences'),
        {},
        {
          page: 'mapping',
          title: 'Audience Mapping',
        }
      )
    );
  },
  audience_page(tenant: number, audience: string) {
    this.hot_reload_view('./mapping/audiences/audience', () =>
      this.transition_view(
        tenant,
        require('./mapping/audiences/audience'),
        { audience },
        {
          page: 'mapping',
          title: 'Edit Audience',
        }
      )
    );
  },
  conversion_mapping(tenant: number) {
    this.hot_reload_view('./mapping/conversion_mapping/landing_page', () =>
      this.transition_view(
        tenant,
        require('./mapping/conversion_mapping/landing_page'),
        {},
        {
          page: 'mapping',
          title: 'Conversion Mapping',
        }
      )
    );
  },
  connectors_overview_conversion_mapping(tenant: number) {
    this.hot_reload_view(
      './mapping/conversion_mapping/configuration_steps/first_step',
      () =>
        this.transition_view(
          tenant,
          require('./mapping/conversion_mapping/configuration_steps/first_step'),
          {},
          {
            page: 'conversion mapping - connectors',
            title: 'Conversion Mapping - connectors',
          }
        )
    );
  },
  standard_parameters_config_conversion_mapping(tenant: number) {
    this.hot_reload_view(
      './mapping/conversion_mapping/configuration_steps/second_step',
      () =>
        this.transition_view(
          tenant,
          require('./mapping/conversion_mapping/configuration_steps/second_step'),
          {},
          {
            page: 'conversion mapping - standard parameters',
            title: 'Conversion Mapping - standard parameters',
          }
        )
    );
  },
  conversion_type_config_conversion_mapping(tenant: number) {
    this.hot_reload_view(
      './mapping/conversion_mapping/configuration_steps/third_step',
      () =>
        this.transition_view(
          tenant,
          require('./mapping/conversion_mapping/configuration_steps/third_step'),
          {},
          {
            page: 'conversion mapping',
            title: 'Conversion Mapping',
          }
        )
    );
  },
  connectors_overview_manage_conversion_mapping(tenant: number) {
    this.hot_reload_view('./mapping/conversion_mapping/display_page', () =>
      this.transition_view(
        tenant,
        require('./mapping/conversion_mapping/display_page'),
        {},
        {
          page: 'conversion mapping',
          title: 'Conversion Mapping',
        }
      )
    );
  },
  data_discovery(tenant: number, source_system: string) {
    window.location.assign(
      `${NEW_ADMIN_URL}/org/${tenant}/data/discovery/${source_system}`
    );
  },
  dashboard(tenant: number) {
    window.location.assign(`${NEW_ADMIN_URL}/org/${tenant}`);
  },
  setup_page(tenant: number) {
    this.hot_reload_view('./predictions/setup', () =>
      this.transition_view(
        tenant,
        require('./predictions/setup'),
        {},
        {
          page: 'setup',
          title: 'Setup',
        }
      )
    );
  },
  configure_push_page(tenant: number) {
    this.hot_reload_view('./integrations/configurePush', () =>
      this.transition_view(
        tenant,
        require('./integrations/configurePush'),
        {
          model: this.tenant,
        },
        {
          page: 'configure',
          title: 'Configure Salesforce',
        }
      )
    );
  },
  results(tenant: number) {
    this.hot_reload_view('./predictions/results', () =>
      this.transition_view(
        tenant,
        require('./predictions/results'),
        {},
        {
          page: 'results',
          title: 'Results',
        }
      )
    );
  },
  bulk_scorer_result(tenant: number, requestid: string) {
    this.hot_reload_view('./bulkScorer', () =>
      this.transition_view(
        tenant,
        require('./bulkScorer'),
        { requestid },
        {
          page: 'bulk result',
          title: 'Bulk Result',
        }
      )
    );
  },
});

export default Mn.AppRouter.extend({
  execute(callback: () => {}, args: any[], name: string) {
    this.controller.execute(callback, args, name);
  },
  appRoutes: {
    // Home
    '(/)': 'direct',
    'org/:tenant/homepage': 'homepage',
    'org/get-started/googlesheet': 'onboarding_googlesheet_redirection', // redirection to have a link without tenant-number for ggsheet onboarding
    'org/:tenant(/)': 'home',
    'org/:tenant/help(/)': 'help',

    // Data Studio
    'org/:tenant/data-studio': 'dataStudio',
    // EVR Insights
    'org/:tenant/evr_insights/:page': 'evrInsights',
    'org/:tenant/evr_insights/:page/configuration': 'evrInsightsConfiguration',
    'org/:tenant/evr_insights/:page/:tab': 'evrInsights',
    'org/:tenant/evr': 'evr',

    // Get started
    'org/:tenant/get-started/intercom': 'onboarding_intercom',
    'org/:tenant/get-started/hubspot': 'onboarding_hubspot',
    'org/:tenant/get-started/googlesheet': 'onboarding_googlesheet',

    // Integrations
    'org/:tenant/integrations(/)': 'integrations',
    'org/:tenant/integrations/:integration(/)': 'integration_setup',
    'org/:tenant/integrations/:integration/overview(/)': 'integration_setup',
    'org/:tenant/integrations/:integration/configuration(/)':
      'integration_setup',
    'org/:tenant/integrations/:integration/superkudu-configuration(/)':
      'integration_setup',
    'org/:tenant/integrations/:integration/pull(/)': 'integration_setup',
    'org/:tenant/integrations/:integration/push(/)': 'integration_setup',
    'org/:tenant/integrations/:integration/data(/)': 'integration_setup',
    'org/:tenant/integrations/:integration/push/configure(/)':
      'configure_push_page',
    'integrations/:integration(/)': 'integration_setup_without_tenant',
    'org/:tenant/integrations/:integration/admin(/)': 'integration_admin',
    'org/:tenant/integrations/:integration/configure/sample(/)':
      'integration_configure_sample',
    'org/:tenant/integrations/:integration/configure(/)':
      'integration_configure',
    'org/:tenant/integrations/:integration/pull/:object(/)':
      'integration_pull_config',
    'org/:tenant/integrations/:integration/workbench(/)':
      'integration_workbench',
    'integrations/:integration/callback(/)': 'integration_callback',

    // Settings & metadata
    'org/:tenant/settings(/)': 'settings_billing',
    'org/:tenant/settings/billing(/)': 'settings_billing_page',
    'org/:tenant/settings/billing/admin(/)': 'settings_billing_admin',
    'org/:tenant/settings/account(/)': 'settings_account',
    'org/:tenant/settings/authentication(/)': 'settings_authentication',
    'org/:tenant/settings/users(/)': 'settings_users',
    'org/:tenant/settings/admin': 'settings_admin',
    'org/:tenant/settings/usage': 'settings_api_usage',
    'org/:tenant/settings/usage/admin': 'settings_api_usage_admin',
    'org/:tenant/settings/privacy(/)': 'settings_privacy',
    'org/:tenant/admin(/)': 'settings_admin',
    'org/:tenant/flags(/)': 'flags',
    'org/:tenant/processes(/)': 'processes',
    'org/:tenant/flags/:flag_name(/)': 'flags',
    'org/:tenant/push(/)': 'push',
    'org/:tenant/push/:pageName(/)': 'push',

    // Smart form campaigns
    'org/:tenant/campaigns(/)': 'campaigns',
    'org/:tenant/campaigns/:campaign_id(/)': 'campaigns',
    'org/:tenant/advanced_campaigns(/)': 'advanced_campaigns',
    'org/:tenant/advanced_campaigns/:campaign_id(/)': 'advanced_campaigns',

    // Prototypes
    'org/:tenant/prototypes(/)': 'prototypes',
    'org/:tenant/sqline_ui(/)': 'sqline_ui',

    // Demo
    'org/:tenant/profiles(/)': 'profiles',
    'org/:tenant/predictions(/)': 'predictions',
    'org/:tenant/predictions/performances/models/:id': 'performances',
    'org/:tenant/predictions/performances/models/:id/diagnostics':
      'diagnostics',
    'org/:tenant/predictions/performances/models/:id/diagnostics/lead_grade':
      'diagnostics_lead_grade',
    'org/:tenant/predictions/performances/models/:id/diagnostics/model(/)(:lead_grade)':
      'diagnostics_model',
    'org/:tenant/predictions/performances/models/:id/admin': 'model_edit',
    'org/:tenant/dashboard(/)': 'dashboard',
    'org/:tenant/setup(/)': 'setup_page',
    'org/:tenant/results(/)': 'results',
    'org/:tenant/data(/)': 'data_overview',
    'org/:tenant/mapping(/)': 'mapping_overview',
    'org/:tenant/mapping/conversion_mapping(/)': 'conversion_mapping',
    'org/:tenant/mapping/conversion_mapping/connector_overview(/)':
      'connectors_overview_conversion_mapping',
    'org/:tenant/mapping/conversion_mapping/standard_parameters(/)':
      'standard_parameters_config_conversion_mapping',
    'org/:tenant/mapping/conversion_mapping/conversion_type(/)':
      'conversion_type_config_conversion_mapping',
    'org/:tenant/mapping/conversion_mapping/overview(/)':
      'connectors_overview_manage_conversion_mapping',
    'org/:tenant/mapping/attribute_mapping/overview(/)':
      'attribute_mapping_overview',
    'org/:tenant/mapping/attribute_mapping/configuration/simple(/)':
      'attribute_mapping_configuration_simple',
    'org/:tenant/mapping/attribute_mapping/configuration/custom(/)':
      'attribute_mapping_configuration_custom',
    'org/:tenant/mapping/event_mapping(/)': 'event_mapping',
    'org/:tenant/mapping/audiences(/)': 'audience_mapping',
    'org/:tenant/mapping/audiences/:audience': 'audience_page',
    'org/:tenant/data/discovery/:source_system(/)': 'data_discovery',

    // Events
    'org/:tenant/mapping/event_mapping/:connector(/)': 'event_mapping_parent',

    //
    'org/:tenant/bulkresult/:requestid(/)': 'bulk_scorer_result',

    // Chatbot
    'org/:tenant/chatbot': 'chatbot',

    'org/:tenant/preferences': 'preferences',
    'org/:tenant/user-settings': 'user_settings',
    'org/:tenant/market-analyser': 'market_analyser',
  },

  initialize() {
    this.controller = new Controller({});
  },
});
